import { zodResolver } from "@hookform/resolvers/zod";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useCreateRaffleRoundMutation } from "Api/Mutations/Raffle/useCreateRaffleRoundMutation";
import { useUpdateRaffleRoundMutation } from "Api/Mutations/Raffle/useUpdateRaffleRoundMutation";
import { DateFormInput } from "Components/Shared/Inputs/Form/DateFormInput";
import { Dropdown } from "Components/Shared/Inputs/Form/Dropdown";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import type { CodeListItem } from "Models/ICodeListDto";
import { type RaffleRoundDto, Region } from "Infrastructure/Api/Api";
import { getApiErrorStatus } from "Infrastructure/Api/ApiUtils";
import {
  getRaffleRoundSchema,
  type RaffleRoundFormData,
} from "Schemas/raffleRoundSchema";
import { type DefaultValues, useForm, useFieldArray } from "react-hook-form";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Admin.Raffle;

type Props = {
  isOpen: boolean;
  /** Null creates a round; a round edits its draft name, date and prizes. */
  round: RaffleRoundDto | null;
  /** Markets with an open main round; a new round anywhere else becomes the market's main round. */
  regionsWithOpenDefault: ReadonlyArray<Region>;
  onClose: () => void;
};

type ContentProps = Omit<Props, "isOpen">;

// No default market on create: the admin must pick it, a wrong one would start the raffle there.
const toFormData = (
  round: RaffleRoundDto | null,
): DefaultValues<RaffleRoundFormData> => ({
  region: round?.region,
  name: round?.name ?? "",
  drawDate: round?.drawDate ? round.drawDate.substring(0, 10) : "",
  rewards: (round?.rewards ?? []).map((reward) => ({
    title: reward.title,
    description: reward.description ?? undefined,
    quantity: reward.quantity ?? undefined,
  })),
});

const RaffleRoundDialogContent = ({
  round,
  regionsWithOpenDefault,
  onClose,
}: ContentProps) => {
  const { t } = useResource();
  const isEdit = round !== null;

  const { control, handleSubmit, watch } = useForm<RaffleRoundFormData>({
    resolver: zodResolver(getRaffleRoundSchema(t)),
    defaultValues: toFormData(round),
  });
  const rewardsField = useFieldArray({ control, name: "rewards" });

  const selectedRegion = watch("region");
  const isNewMainRound =
    !isEdit &&
    !!selectedRegion &&
    !regionsWithOpenDefault.includes(selectedRegion);

  const {
    mutate: createRound,
    isPending: isCreating,
    isSuccess: isCreated,
  } = useCreateRaffleRoundMutation({
    onSuccess: () => {
      toast.success(t(RESOURCES.Messages.CreateSuccess));
      onClose();
    },
    onError: () => toast.error(t(RESOURCES.Messages.CreateError)),
  });

  const { mutate: updateRound, isPending: isUpdating } =
    useUpdateRaffleRoundMutation({
      onSuccess: () => {
        toast.success(t(RESOURCES.Messages.UpdateSuccess));
        onClose();
      },
      onError: (error) => {
        // 409: no longer a draft, and no round returns to draft, so the edit can never apply.
        if (getApiErrorStatus(error) === 409) {
          toast.error(t(RESOURCES.Messages.UpdateConflict));
          onClose();
          return;
        }
        toast.error(t(RESOURCES.Messages.UpdateError));
      },
    });

  const codeListRegions: Array<CodeListItem> = Object.values(Region).map(
    (region) => ({ code: region, name: region }),
  );

  const onSubmit = (data: RaffleRoundFormData) => {
    // Already yyyy-MM-dd: sent as-is, since a Date round-trip would shift the day west of UTC.
    const drawDate = data.drawDate || null;

    if (isEdit) {
      updateRound({
        publicID: round.publicID,
        data: {
          name: data.name,
          drawDate,
          rewards: data.rewards.map((reward, index) => ({
            order: index + 1,
            title: reward.title,
            description: reward.description || null,
            quantity:
              reward.quantity !== undefined &&
              !Number.isNaN(reward.quantity) &&
              reward.quantity >= 1
                ? Math.floor(reward.quantity)
                : null,
          })),
        },
      });
      return;
    }

    createRound({
      region: data.region,
      name: data.name,
      drawDate,
    });
  };

  return (
    // noValidate: zod validates; the browser's number check would block submit untranslated.
    <form noValidate onSubmit={handleSubmit(onSubmit)}>
      <DialogTitle>
        {isEdit ? t(RESOURCES.Form.EditTitle) : t(RESOURCES.Form.CreateTitle)}
      </DialogTitle>
      <DialogContent>
        <Stack gap={2} mt={1}>
          {!isEdit && (
            <Dropdown
              control={control}
              name="region"
              label={t(RESOURCES.Form.Region)}
              codeList={codeListRegions}
            />
          )}
          {isNewMainRound && (
            <Alert severity="warning">
              {t(RESOURCES.Form.MainRoundWarning)}
            </Alert>
          )}
          <FormInput
            control={control}
            name="name"
            label={t(RESOURCES.Form.Name)}
            maxLength={100}
          />
          <DateFormInput
            control={control}
            name="drawDate"
            label={t(RESOURCES.Form.DrawDate)}
          />
          {isEdit && (
            <>
              <Typography variant="subtitle2">
                {t(RESOURCES.Form.Rewards)}
              </Typography>
              {rewardsField.fields.map((field, index) => (
                <Stack
                  key={field.id}
                  direction={{ xs: "column", sm: "row" }}
                  gap={1}
                  alignItems={{ sm: "flex-start" }}
                >
                  <Box flex={2}>
                    <FormInput
                      control={control}
                      name={`rewards.${index}.title`}
                      label={t(RESOURCES.Form.RewardTitle)}
                      maxLength={200}
                    />
                  </Box>
                  <Box flex={2}>
                    <FormInput
                      control={control}
                      name={`rewards.${index}.description`}
                      label={t(RESOURCES.Form.RewardDescription)}
                      maxLength={2000}
                    />
                  </Box>
                  <Box flex={1}>
                    <FormInput
                      control={control}
                      name={`rewards.${index}.quantity`}
                      label={t(RESOURCES.Form.RewardQuantity)}
                      type="number"
                      min={0}
                    />
                  </Box>
                  <IconButton
                    aria-label={t(RESOURCES.Form.RemoveReward)}
                    onClick={() => rewardsField.remove(index)}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Stack>
              ))}
              <Box>
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => rewardsField.append({ title: "" })}
                >
                  {t(RESOURCES.Form.AddReward)}
                </Button>
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t(RESOURCES.Form.Cancel)}</Button>
        <Button
          type="submit"
          variant="contained"
          disabled={isCreating || isUpdating || isCreated}
        >
          {t(RESOURCES.Form.Submit)}
        </Button>
      </DialogActions>
    </form>
  );
};

export const RaffleRoundDialog = ({
  isOpen,
  round,
  regionsWithOpenDefault,
  onClose,
}: Props) => (
  <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
    {/* Keyed so each open starts from that round's values, or an empty form. */}
    <RaffleRoundDialogContent
      key={round?.publicID ?? "create"}
      round={round}
      regionsWithOpenDefault={regionsWithOpenDefault}
      onClose={onClose}
    />
  </Dialog>
);
